import React, {useCallback, useMemo, useState} from "react";
import {connect} from "react-redux";
import {useHistory, useLocation, useParams} from "react-router-dom";
import Alert from "react-s-alert";
import {Button, H2, Text, styled, theme} from "@reside/ui";
import {RoleNameEnum} from "../../utils/types/common";
import {TableUserDto} from "../../utils/types/user";
import {AppState, select} from "../../store";
import {DocumentTitle} from "../../atoms/document-title";
import {AdminRoles} from "../../atoms/admin-roles";
import {Spinner} from "../../atoms/spinner";
import {Footer} from "../../atoms/footer";
import {ReportingLayout} from "./reporting-layout";
import {PermissionManagementForm} from "./atoms/permission-management-modal/PermissionManagementForm";
import {
  formatPendingChangesLabel,
  useUserPermissionsMutation,
} from "./atoms/permission-management-modal/hooks/useUserPermissionsMutation";
import {useUserQuery} from "../page-admin-user-edit/hooks/useUserQuery";
import {
  mapUserDtoToTableUser,
  PERMISSIONS_MANAGEMENT_LIST_PATH,
  PermissionsUserDetailHeader,
} from "./atoms/permissions-user-detail";
import {QuicksightDashboardDeferredSaveHandlers} from "./atoms/quicksight-dashboards-macro-checkbox";

type LocationState = Readonly<{
  user?: TableUserDto;
}>;

type Props = {
  isResideAdmin: boolean;
};

const PageReportingPermissionsUserDetailRenderer = ({isResideAdmin}: Props) => {
  const history = useHistory();
  const {userId} = useParams<{userId: string}>();
  const location = useLocation<LocationState>();
  const listUser = location.state?.user;

  const {
    data: fetchedUser,
    isLoading,
    error,
  } = useUserQuery({
    userId,
  });

  const user = useMemo(() => {
    if (fetchedUser) {
      return mapUserDtoToTableUser(fetchedUser);
    }

    return listUser;
  }, [fetchedUser, listUser]);

  const mutationUser = user ?? ({id: userId, permissions: []} as TableUserDto);

  const {
    activePermissions,
    discardChanges,
    isPermissionPending,
    isSaving,
    onSetPermission,
    pendingChangeCount,
    save,
  } = useUserPermissionsMutation(mutationUser, {autoSave: false});

  const [
    quicksightDeferredSaveHandlers,
    setQuicksightDeferredSaveHandlers,
  ] = useState<QuicksightDashboardDeferredSaveHandlers | null>(null);

  const handleDeferredQuicksightSaveHandlersChange = useCallback(
    (handlers: QuicksightDashboardDeferredSaveHandlers | null) => {
      setQuicksightDeferredSaveHandlers(current => {
        const nextCount = handlers?.pendingChangeCount ?? 0;
        const currentCount = current?.pendingChangeCount ?? 0;

        if (Boolean(current) === Boolean(handlers) && currentCount === nextCount) {
          return current;
        }

        return handlers;
      });
    },
    [],
  );

  const quicksightPendingChangeCount =
    quicksightDeferredSaveHandlers?.pendingChangeCount ?? 0;
  const totalPendingChangeCount =
    pendingChangeCount + quicksightPendingChangeCount;
  const [isSavingQuicksightDashboards, setIsSavingQuicksightDashboards] =
    useState(false);

  const goBackToList = () => {
    history.push(PERMISSIONS_MANAGEMENT_LIST_PATH);
  };

  const handleCancel = () => {
    discardChanges();
    quicksightDeferredSaveHandlers?.discard();
    goBackToList();
  };

  const handleSave = async () => {
    const hasPermissionChanges = pendingChangeCount > 0;
    const hasQuicksightChanges = quicksightPendingChangeCount > 0;

    if (!hasPermissionChanges && !hasQuicksightChanges) {
      return;
    }

    try {
      if (hasQuicksightChanges) {
        setIsSavingQuicksightDashboards(true);
        await quicksightDeferredSaveHandlers?.save();
      }

      if (hasPermissionChanges) {
        save();
      } else {
        Alert.success("Permissions saved successfully.");
      }
    } finally {
      setIsSavingQuicksightDashboards(false);
    }
  };

  const isSavingChanges = isSaving || isSavingQuicksightDashboards;

  return (
    <ReportingLayout isActionRowVisible={false}>
      <DocumentTitle title="Reside - Admin - User Permissions" />
      <AdminRoles
        roles={[RoleNameEnum.RESIDE_ADMIN, RoleNameEnum.ORGANIZATION_ADMIN]}
      >
        <PageContainer>
          {!user && isLoading ? (
            <Spinner />
          ) : error || !user ? (
            <ErrorState>
              <H2>Error while fetching user</H2>
              <Button color="primary" onClick={goBackToList}>
                Back to list
              </Button>
            </ErrorState>
          ) : (
            <>
              <PermissionsUserDetailHeader
                user={user}
                activePermissions={activePermissions}
                includeOtherGroup={isResideAdmin}
              />
              <FormSection>
                <PermissionManagementForm
                  userId={user.id!}
                  email={user.email ?? ""}
                  activePermissions={activePermissions}
                  deferQuicksightSave
                  isPermissionPending={isPermissionPending}
                  onDeferredQuicksightSaveHandlersChange={
                    handleDeferredQuicksightSaveHandlersChange
                  }
                  onSetPermission={onSetPermission}
                />
              </FormSection>
              <StickyFooter
                left={
                  totalPendingChangeCount > 0 ? (
                    <PendingChangesText>
                      {formatPendingChangesLabel(totalPendingChangeCount)}
                    </PendingChangesText>
                  ) : null
                }
                right={
                  <FooterButtons>
                    <Button outline color="primary" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      disabled={
                        isSavingChanges || totalPendingChangeCount === 0
                      }
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                  </FooterButtons>
                }
              />
            </>
          )}
        </PageContainer>
      </AdminRoles>
    </ReportingLayout>
  );
};

const mapStateToProps = (state: AppState) => ({
  isResideAdmin: select.adminSession.isResideAdmin(state),
});

export const PageReportingPermissionsUserDetail = connect(mapStateToProps)(
  PageReportingPermissionsUserDetailRenderer,
);

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 8px 0 0;
`;

const FormSection = styled.div`
  flex: 1;
  padding-bottom: 40px;
`;

const StickyFooter = styled(Footer)`
  align-items: center;
  background-color: #ffffff;
  box-shadow: 0 -12px 12px 0 ${theme.color.white},
    0 -12px 24px 0 ${theme.color.white};
  margin-top: 32px;
  padding: 16px 0;
  width: 100%;
  z-index: 2;

  @media (min-width: ${({theme}) => theme.breakpoint.md}px) {
    padding: 24px 0;
  }
`;

const PendingChangesText = styled(Text)`
  color: ${theme.color.darkBlue100};
  font-size: 15px;
  line-height: 24px;
  margin-left: 24px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  padding-right: 80px;
`;

const ErrorState = styled.div`
  padding: 100px 0;
  text-align: center;

  button {
    margin: 24px auto 0;
  }
`;
